import { OpenCVWrapper, MatSpace, MatType } from './opencv-wrapper/opencv-wrapper';
import * as d3 from 'd3';

import { ThermalImageMode } from 'reducers';

export function isThermalData(filename: string): boolean {
    return filename.toLowerCase().endsWith('.rdat');
}
export class ThermalDataRenderer {
    isReady: boolean;
    cv2: OpenCVWrapper;
    minTemp: number;
    maxTemp: number;
    cmap: CallableFunction;
    scale: CallableFunction;
    spectrumScale: CallableFunction;
    otherScale: CallableFunction;
    ref: Int16Array | null;
    refFrame: number;
    ambient: number;

    constructor() {
        this.isReady = false;
        this.cv2 = new OpenCVWrapper();

        this.minTemp = 16;
        this.maxTemp = 30;
        this.ref = null;
        this.refFrame = 0;
        this.ambient = 23;
        this.cmap = d3.scaleSequential().domain([0.0, 1.0]).interpolator(d3.interpolateTurbo);//.interpolateViridis);
        this.scale = d3.scaleLinear().domain([this.minTemp, this.maxTemp]).range([0.0, 1.0])

        this.spectrumScale = d3.scaleLinear()
          .domain([0.0, 0.35, 0.495, 0.5, 0.5025, 0.575, 0.675, 0.8, 1.0])
          .range([
            'rgb(209, 209, 209)',
            'rgb(0, 0, 0)',
            'rgb(32, 0, 158)',
            'rgb(32, 0, 158)',
            'rgb(32, 0, 158)',
            'rgb(0, 238, 171)',
            'rgb(254, 253, 66)',
            'rgb(254, 0, 0)',
            'rgb(197, 0, 254)'
            ] as any)
          .clamp(true);

          this.otherScale = d3.scaleOrdinal()
          .range([
            // category10, with light grey for 0.
            "#d3d3d3",
            "#1f77b4",
            "#ff7f0e",
            "#2ca02c",
            "#d62728",
            "#9467bd",
            "#8c564b",
            "#e377c2",
            "#7f7f7f",
            "#bcbd22",
            "#17becf",
            // set3
            "#8dd3c7",
            "#ffffb3",
            "#bebada",
            "#fb8072",
            "#80b1d3",
            "#fdb462",
            "#b3de69",
            "#fccde5",
            "#d9d9d9",
            "#bc80bd",
            "#ccebc5",
            "#ffed6f"
          ]);
    }

    public async initializeOpenCV() {
        console.log('Init OpenCV for thermal-data')

        try {
            await this.cv2.initialize((p: number) => {});
            this.isReady = true;
            console.log('OpenCV initialized')

        } catch(error: any) {
            console.log(`Error initializing OpenCV: ${error}`);
        }
    }

    private updateScale(minTemp: number, maxTemp: number) {
        let change = false;

        if (this.minTemp != minTemp) {
            this.minTemp = minTemp;
            change = true;
        }

        if (this.maxTemp != maxTemp) {
            this.maxTemp = maxTemp;
            change = true;
        }

        if (change) {
            this.scale = d3.scaleLinear().domain([this.minTemp, this.maxTemp]).range([0.0, 1.0])
        }
    }

    public async renderRawFrame(frameNumber: number, imageData: any, ctsRendering: any): Promise<ImageBitmap> {
        const cv = this.cv2.cv;
        let useReference = ctsRendering.thermalMode === ThermalImageMode.TMR? true : false;
        let plaMode = ctsRendering.thermalMode === ThermalImageMode.PLA? true: false;
        let updateReference = false;
        let offset = 0;

        if (plaMode && !imageData.tmr8) {
            // fallback to hack if no tmr
            useReference = true;
        }

        // if reference mode, we only update reference if we are given the correct reference frame, else display will revert to RAW mode temporarily
        if (useReference) {
            updateReference = (frameNumber == ctsRendering.refFrame1) && (this.refFrame != ctsRendering.refFrame1 || this.ref == null);
        }

        this.updateScale(ctsRendering.minTemp, ctsRendering.maxTemp);

        // console.log(imageData)

        let raw = imageData.raw as Int16Array;
        let tmr8 = null;

        if (imageData.tmr8) {
            tmr8 = imageData.tmr8 as Int8Array;
        }

        let rawMeta = imageData.raw_md;
        let width = rawMeta.c;
        let height = rawMeta.r;
        let tf = rawMeta.tf;
        let rgb = new Uint8ClampedArray(4*width*height); // rgb mapped thermal image
        let roi = new cv.Rect(rawMeta.coff, rawMeta.roff, rawMeta.vc, rawMeta.vr); // cropping region

        // update reference image (if using)
        if (updateReference) {
            console.log(`Updating reference image to frame ${frameNumber}`)
            let tempMat = cv.matFromArray(height, width, cv.CV_16SC1, Array.from(raw));
            // this.ambient = cv.mean(tempMat.roi(roi))[0]/tf;
            this.ref = tempMat.data16S;
            this.refFrame = frameNumber;
        }

        if (useReference && this.ref!=null) {
            offset = this.ambient;
        }

        let plaGood = false;

        if (plaMode && tmr8) {
            plaGood = true;

            tmr8.forEach((x, index) => {
                let val = (x)/tf + 0.5;
                let col = d3.color(this.spectrumScale(val))?.rgb();

                // console.log(`${index} :: ${val} :: ${col}`)

                if (col) {
                    rgb[index*4+0] = col.r;
                    rgb[index*4+1] = col.g;
                    rgb[index*4+2] = col.b;
                    rgb[index*4+3] = col.opacity*255;
                }
                else {
                    rgb[index*4+0] = 0;
                    rgb[index*4+1] = 0;
                    rgb[index*4+2] = 0;
                    rgb[index*4+3] = 255;
                }
            });
        }
        else {
            raw.forEach((x, index) => {
                let refVal = 0;

                if (useReference && this.ref!=null) {
                    refVal = this.ref? this.ref[index] : 0;
                }

                let val = null;
                let col = null;

                if (useReference) {
                    // TMR mode
                    val = ((x - refVal)/tf)  / (this.maxTemp - this.minTemp) + 0.5;
                    col = d3.color(this.spectrumScale(val))?.rgb();
                }
                else {
                    // RAW mode
                    val = (x - refVal)/tf + offset;
                    col = d3.color(this.cmap(this.scale(val)))?.rgb();
                }

                if (col) {
                    rgb[index*4+0] = col.r;
                    rgb[index*4+1] = col.g;
                    rgb[index*4+2] = col.b;
                    rgb[index*4+3] = col.opacity*255;
                }
                else {
                    rgb[index*4+0] = 0;
                    rgb[index*4+1] = 0;
                    rgb[index*4+2] = 0;
                    rgb[index*4+3] = 255;
                }
            });
        }

        // test full frame output
        //return createImageBitmap(new ImageData(rgb, rawMeta.c, rawMeta.r));

        let rgbMat = this.cv2.mat.fromData(width, height, MatType.CV_8UC4, Array.from(rgb));

        if (plaGood) {
            // tmr8 is already cropped
            let out = new Uint8ClampedArray(rgbMat.data);
            return createImageBitmap(new ImageData(out, rawMeta.vc, rawMeta.vr));
        }

        // otherwise we need to crop
        let rgbCropped = rgbMat.roi(roi).clone();

        // always use cropped size to ensure annotations are consistent
        let out = new Uint8ClampedArray(rgbCropped.data);
        return createImageBitmap(new ImageData(out, rawMeta.vc, rawMeta.vr));





        /*
        if (useReference) {
            // cropped size
            let out = new Uint8ClampedArray(rgbCropped.data);
            return createImageBitmap(new ImageData(out, rawMeta.vc, rawMeta.vr));
        }
        else {
            // upcropped size
            let out = new Uint8ClampedArray(rgbMat.data);
            return createImageBitmap(new ImageData(out, width, height));
        }*/

        /*
        // test pattern for debugging
        const cols = 20;
        const rows = 10;

        let out = new Uint8ClampedArray(cols*rows*4).fill(0);
        let mod = (i:number, len:number) => ((i % len) + len) % len;

        for (let i=0; i<rows; i++) {
            for (let j=0; j<cols; j++) {
                if (mod(j,2)==0) {
                    console.log(`${i}, ${j}, even`)
                    out[4*(i*cols+j)]=0;
                }
                else {
                    console.log(`${i}, ${j}, odd`)
                    out[4*(i*cols+j)]=255;
                }
                out[4*(i*cols+j)+3]=255;
            }
        }

        return createImageBitmap(new ImageData(out, cols,rows));
        */
    }

}